import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Minus, Plus, Tag, ArrowLeft, Ticket, Users, Info } from 'lucide-react';
import { publicApi } from '@services/publicApi';
import { usePublicStore } from '@store/publicStore';
import { formatDate, formatDateTime, formatCurrency } from '@utils';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import type { TicketType } from '@types';

export default function PublicEventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addToCart, cartItems, cartEventId } = usePublicStore();
  const [quantities, setQuantities] = useState<Record<number, number>>({});

  const { data, isLoading } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: () => publicApi.getEventBySlug(slug!),
    enabled: !!slug,
  });

  const event = data?.data;

  useEffect(() => {
    if (event) {
      document.title = `${event.name} - Get Tickets`;
    }
  }, [event]);

  // Init quantities from cart if same event
  useEffect(() => {
    if (event && cartEventId === event.id && cartItems.length > 0) {
      const q: Record<number, number> = {};
      cartItems.forEach(item => { q[item.ticketTypeId] = item.quantity; });
      setQuantities(q);
    }
  }, [event, cartEventId, cartItems]);

  const updateQty = (ticketType: TicketType, delta: number) => {
    setQuantities(prev => {
      const current = prev[ticketType.id] || 0;
      const next = Math.max(0, Math.min(current + delta, ticketType.maxPerOrder || ticketType.availableCount, ticketType.availableCount));
      return { ...prev, [ticketType.id]: next };
    });
  };

  const totalSelected = Object.values(quantities).reduce((sum, q) => sum + q, 0);
  const subtotal = event?.ticketTypes?.reduce((sum, tt) => {
    const qty = quantities[tt.id] || 0;
    return sum + tt.price * qty;
  }, 0) || 0;

  const handleGetTickets = () => {
    if (!event) return;

    const selectedTypes = event.ticketTypes?.filter(tt => (quantities[tt.id] || 0) > 0) || [];
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one ticket');
      return;
    }

    // Validate minimums
    for (const tt of selectedTypes) {
      const qty = quantities[tt.id];
      if (tt.minPerOrder > 0 && qty < tt.minPerOrder) {
        toast.error(`Minimum ${tt.minPerOrder} tickets for ${tt.name}`);
        return;
      }
    }

    // Add each selected type to cart
    selectedTypes.forEach(tt => {
      addToCart(event.id, event.slug, {
        ticketTypeId: tt.id,
        ticketTypeName: tt.name,
        quantity: quantities[tt.id],
        unitPrice: tt.price,
        currency: event.currency,
        maxPerOrder: tt.maxPerOrder || tt.availableCount,
        minPerOrder: tt.minPerOrder || 1,
        availableCount: tt.availableCount,
      });
    });

    navigate('/checkout');
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-2xl animate-pulse mb-8" />
        <div className="space-y-4">
          <div className="h-8 w-2/3 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
          <div className="h-4 w-1/3 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
          <div className="h-20 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">Event Not Found</h2>
        <Link to="/events" className="text-primary-600 hover:text-primary-700 font-medium">Browse all events</Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Hero Image */}
      <div className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-br from-primary-600 to-primary-900 overflow-hidden">
        {event.featuredImage ? (
          <>
            <img src={event.featuredImage} alt={event.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 to-primary-800" />
        )}

        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 lg:p-12">
          <div className="max-w-5xl mx-auto">
            <Link to="/events" className="inline-flex items-center gap-1 text-white/80 hover:text-white text-sm mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Events
            </Link>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">{event.name}</h1>
            <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {formatDate(event.startDate, 'EEEE, MMMM d, yyyy')}
              </span>
              {event.venue && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" />
                  {event.venue.name}, {event.venue.city}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Event Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {event.description && (
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">About This Event</h2>
                <div className="prose prose-gray dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 whitespace-pre-line">
                  {event.description}
                </div>
              </div>
            )}

            {/* Event Details */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold text-gray-900 dark:text-white">Event Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Date & Time</div>
                    <div className="text-gray-500">{formatDateTime(event.startDate)}</div>
                    {event.endDate && <div className="text-gray-500">to {formatDateTime(event.endDate)}</div>}
                  </div>
                </div>
                {event.venue && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-primary-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Venue</div>
                      <div className="text-gray-500">{event.venue.name}</div>
                      <div className="text-gray-500">{event.venue.address}, {event.venue.city}</div>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <Users className="w-5 h-5 text-primary-500 mt-0.5" />
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">Capacity</div>
                    <div className="text-gray-500">{event.capacity} attendees</div>
                  </div>
                </div>
                {event.doorsOpenAt && (
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-primary-500 mt-0.5" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">Doors Open</div>
                      <div className="text-gray-500">{formatDateTime(event.doorsOpenAt)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Types */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Select Tickets</h2>
              <div className="space-y-3">
                {event.ticketTypes?.map(tt => {
                  const qty = quantities[tt.id] || 0;
                  const soldOut = tt.availableCount <= 0;
                  const maxReached = qty >= (tt.maxPerOrder || tt.availableCount);

                  return (
                    <div
                      key={tt.id}
                      className={`border rounded-xl p-5 transition-colors ${
                        qty > 0
                          ? 'border-primary-300 bg-primary-50/50 dark:border-primary-700 dark:bg-primary-900/10'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
                      } ${soldOut ? 'opacity-60' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-gray-900 dark:text-white">{tt.name}</h4>
                            {tt.isEarlyBird && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Early Bird</span>
                            )}
                            {tt.isGroupTicket && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">Group ({tt.groupSize})</span>
                            )}
                          </div>
                          {tt.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{tt.description}</p>
                          )}
                          <div className="flex items-center gap-3 text-sm">
                            <span className="font-bold text-lg text-gray-900 dark:text-white">
                              {tt.price === 0 ? 'Free' : formatCurrency(tt.price, tt.currency)}
                            </span>
                            {tt.compareAtPrice && tt.compareAtPrice > tt.price && (
                              <span className="text-gray-400 line-through text-sm">
                                {formatCurrency(tt.compareAtPrice, tt.currency)}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {soldOut ? 'Sold Out' : `${tt.availableCount} available`}
                          </div>
                        </div>

                        {/* Quantity Selector */}
                        {!soldOut && (
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => updateQty(tt, -1)}
                              disabled={qty <= 0}
                              className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-semibold text-gray-900 dark:text-white">{qty}</span>
                            <button
                              onClick={() => updateQty(tt, 1)}
                              disabled={maxReached}
                              className="w-9 h-9 rounded-lg border border-gray-200 dark:border-gray-700 flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-2xl p-6">
              <h3 className="font-bold text-gray-900 dark:text-white mb-4">Order Summary</h3>

              {totalSelected === 0 ? (
                <div className="text-center py-6">
                  <Ticket className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Select tickets to continue</p>
                </div>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {event.ticketTypes?.filter(tt => (quantities[tt.id] || 0) > 0).map(tt => (
                      <div key={tt.id} className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">
                          {quantities[tt.id]}x {tt.name}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {formatCurrency(tt.price * quantities[tt.id], event.currency)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mb-6">
                    <div className="flex justify-between font-bold text-lg">
                      <span className="text-gray-900 dark:text-white">Total</span>
                      <span className="text-gray-900 dark:text-white">{formatCurrency(subtotal, event.currency)}</span>
                    </div>
                  </div>

                  <button
                    onClick={handleGetTickets}
                    className="w-full py-3.5 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700 active:bg-primary-800 transition-colors shadow-lg shadow-primary-600/25"
                  >
                    Get Tickets
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
